"use client"
import React, { useState, useEffect } from "react"
import InputField from "../../../components/common/InputField"
import {
  Upload,
  Bold,
  Italic,
  Strikethrough,
  Minus,
  Link2,
  Quote,
  Code2,
  Braces,
  Image as ImageIcon,
  Table2,
  List,
  ListOrdered,
  ListChecks,
  CircleHelp,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Maximize2,
  Minimize2,
} from "lucide-react"

import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Markdown } from "@tiptap/markdown"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import { TableKit } from "@tiptap/extension-table"
import { TaskList, TaskItem } from "@tiptap/extension-list"
import TextAlign from "@tiptap/extension-text-align"
import Underline from "@tiptap/extension-underline"

interface PackageDetailsStepProps {
  data: any
  onChange: (data: any) => void
  onNext: () => void
  onSaveDraft?: () => void
  formRef: React.RefObject<HTMLFormElement | null>
}

const PackageDetailsStep: React.FC<PackageDetailsStepProps> = ({ 
  data, 
  onChange, 
  onNext,
  onSaveDraft,
  formRef
}) => {
  const [coverPhotoPreviewUrl, setCoverPhotoPreviewUrl] = useState<string | null>(null)
  const [bannerPhotoPreviewUrl, setBannerPhotoPreviewUrl] = useState<string | null>(null)
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false)
  
  // Initialize preview URLs
useEffect(() => {
  setCoverPhotoPreviewUrl(data.coverUrl || null)
  setBannerPhotoPreviewUrl(data.bannerUrl || null)
}, [data.coverUrl, data.bannerUrl])


  const handleInputChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value })
  }

  const descriptionEditor = useEditor({
  extensions: [
    StarterKit,

    Markdown,

    Underline,

    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
    }),

    Image.configure({
      inline: false,
      allowBase64: true,
    }),

    TableKit.configure({
      table: {
        resizable: true,
      },
    }),

    TaskList,

    TaskItem.configure({
      nested: true,
    }),

    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
  ],

  content: data.description || "",
  contentType: "markdown",
  immediatelyRender: false,

  onUpdate: ({ editor }) => {
    const markdown = editor.getMarkdown()
    handleInputChange("description", markdown)
  },
})

  const handleCoverPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0]

  if (!file) return

  if (!file.type.match("image.*")) {
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    return
  }

  const reader = new FileReader()

  reader.onloadend = () => {
    const previewUrl = reader.result as string

    onChange({
      ...data,
      coverImage: file,
      coverUrl: previewUrl,
    })

    setCoverPhotoPreviewUrl(previewUrl)
  }

  reader.readAsDataURL(file)
}

  const handleBannerPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0]

  if (!file) return

  if (!file.type.match("image.*")) {
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    return
  }

  const reader = new FileReader()

  reader.onloadend = () => {
    const previewUrl = reader.result as string

    // Save the actual File for API upload
    // AND save the Base64 preview in parent state
    onChange({
      ...data,
      bannerImage: file,
      bannerUrl: previewUrl,
    })

    // Keep local preview immediately visible
    setBannerPhotoPreviewUrl(previewUrl)
  }

  reader.readAsDataURL(file)
}


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!data.title || !data.description) {
      return
    }
    onNext()
  }

  return (
    <form 
      ref={formRef} 
      onSubmit={handleSubmit} 
      className="space-y-8 max-w-4xl mx-auto"
    >
      <div className="text-center sm:text-left">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3">Package Details</h2>
        <p className="text-gray-600 text-lg">Provide basic information about your package</p>
      </div>
      
      {/* Cover Photo Upload */}
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-gray-700">
          Cover photo <span className="text-red-500">*</span>
        </label>
        <div className="w-full border-2 border-dashed border-purple-300 rounded-xl hover:border-purple-400 transition-all duration-300 bg-gradient-to-br from-purple-50 to-blue-50">
          <input
            type="file"
            accept="image/*"
            onChange={handleCoverPhotoUpload}
            className="hidden"
            id="cover-photo-upload"
            required={!coverPhotoPreviewUrl && !data.coverUrl}
          />
          <label htmlFor="cover-photo-upload" className="w-full cursor-pointer block">
            {coverPhotoPreviewUrl || data.coverUrl ? (
              <div className="relative w-full aspect-[16/9] overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gray-100">
                  <img
                    src={coverPhotoPreviewUrl || data.coverUrl}
                    alt="Package cover photo preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setCoverPhotoPreviewUrl(null)
                    handleInputChange("coverImage", null)
                    handleInputChange("coverUrl", null)
                  }}
                  className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="relative w-full aspect-[16/9] overflow-hidden rounded-xl bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col items-center justify-center text-center mx-auto p-6 hover:from-purple-100 hover:to-blue-100 transition-all duration-300">
                <div className="bg-white rounded-full p-4 shadow-lg mb-4">
                  <Upload className="text-purple-600" size={32} />
                </div>
                <p className="text-gray-700 font-semibold text-base mb-2">Click to upload cover photo</p>
                <p className="text-sm text-gray-500 text-center">
                  Recommended: 1280x720px (16:9)<br />
                  <span className="text-xs">Max size: 10MB</span>
                </p>
              </div>
            )}
          </label>
        </div>
      </div>
      
      {/* Package Title */}
      <InputField
        type="text"
        label="Package title"
        name="title"
        value={data.title || ""}
        onChange={(e) => handleInputChange("title", e.target.value)}
        required
        placeholder="Enter package title"
      /> 
      
{/* Package Description */}
<div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">

  <label className="flex items-center gap-1 text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">
    <span>Package description</span>
    <span className="text-red-500">*</span>
  </label>

  <div
    className={`course-description-editor ${
      isEditorFullscreen
        ? "course-description-editor-fullscreen"
        : ""
    }`}
  >

    {/* Toolbar */}
    <div className="course-description-toolbar">

      {/* Bold */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor?.chain().focus().toggleBold().run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("bold") ? "active" : ""
        }`}
        title="Bold"
      >
        <Bold size={17} strokeWidth={2.5} />
      </button>

      {/* Italic */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor?.chain().focus().toggleItalic().run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("italic") ? "active" : ""
        }`}
        title="Italic"
      >
        <Italic size={17} strokeWidth={2.5} />
      </button>

      {/* Strikethrough */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor?.chain().focus().toggleStrike().run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("strike") ? "active" : ""
        }`}
        title="Strikethrough"
      >
        <Strikethrough size={17} strokeWidth={2.5} />
      </button>

      {/* Horizontal Line */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor?.chain().focus().setHorizontalRule().run()
        }
        className="editor-tool"
        title="Horizontal Line"
      >
        <Minus size={18} strokeWidth={2.5} />
      </button>

      <div className="editor-toolbar-divider" />

      {/* Link */}
      <button
        type="button"
        onClick={() => {
          const previousUrl =
            descriptionEditor?.getAttributes("link").href

          const url = window.prompt(
            "Enter URL",
            previousUrl || "https://"
          )

          if (url === null) return

          if (url === "") {
            descriptionEditor
              ?.chain()
              .focus()
              .extendMarkRange("link")
              .unsetLink()
              .run()

            return
          }

          descriptionEditor
            ?.chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run()
        }}
        className={`editor-tool ${
          descriptionEditor?.isActive("link") ? "active" : ""
        }`}
        title="Insert Link"
      >
        <Link2 size={18} strokeWidth={2.5} />
      </button>

      {/* Blockquote */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .toggleBlockquote()
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("blockquote") ? "active" : ""
        }`}
        title="Blockquote"
      >
        <Quote size={18} strokeWidth={2.5} />
      </button>

      {/* Inline Code */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .toggleCode()
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("code") ? "active" : ""
        }`}
        title="Inline Code"
      >
        <Code2 size={18} strokeWidth={2.5} />
      </button>

      {/* Code Block */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .toggleCodeBlock()
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("codeBlock") ? "active" : ""
        }`}
        title="Code Block"
      >
        <Braces size={18} strokeWidth={2.5} />
      </button>

      {/* Image */}
      <button
        type="button"
        onClick={() => {
          const url = window.prompt("Enter image URL")

          if (url) {
            descriptionEditor
              ?.chain()
              .focus()
              .setImage({ src: url })
              .run()
          }
        }}
        className="editor-tool"
        title="Insert Image"
      >
        <ImageIcon size={18} strokeWidth={2.3} />
      </button>

      {/* Table */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .insertTable({
              rows: 3,
              cols: 3,
              withHeaderRow: true,
            })
            .run()
        }
        className="editor-tool"
        title="Insert Table"
      >
        <Table2 size={18} strokeWidth={2.3} />
      </button>

      {/* Bullet List */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .toggleBulletList()
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("bulletList")
            ? "active"
            : ""
        }`}
        title="Bullet List"
      >
        <List size={18} strokeWidth={2.3} />
      </button>

      {/* Numbered List */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .toggleOrderedList()
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("orderedList")
            ? "active"
            : ""
        }`}
        title="Numbered List"
      >
        <ListOrdered size={18} strokeWidth={2.3} />
      </button>

      {/* Checklist */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .toggleTaskList()
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive("taskList")
            ? "active"
            : ""
        }`}
        title="Checklist"
      >
        <ListChecks size={18} strokeWidth={2.3} />
      </button>

      <div className="editor-toolbar-divider" />

      {/* Help */}
      <button
        type="button"
        onClick={() => {
          window.alert(
            "Package Description Editor\n\n" +
            "Use the toolbar to format your package description.\n\n" +
            "• Bold / Italic / Strike\n" +
            "• Links and blockquotes\n" +
            "• Code and code blocks\n" +
            "• Images and tables\n" +
            "• Bullet, numbered and checklist lists\n" +
            "• Text alignment\n" +
            "• Undo / Redo"
          )
        }}
        className="editor-tool"
        title="Editor Help"
      >
        <CircleHelp size={18} strokeWidth={2.3} />
      </button>

      <div className="editor-toolbar-spacer" />

      {/* Align Left */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .setTextAlign("left")
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive({
            textAlign: "left",
          })
            ? "active"
            : ""
        }`}
        title="Align Left"
      >
        <AlignLeft size={18} strokeWidth={2.3} />
      </button>

      {/* Align Center */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .setTextAlign("center")
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive({
            textAlign: "center",
          })
            ? "active"
            : ""
        }`}
        title="Align Center"
      >
        <AlignCenter size={18} strokeWidth={2.3} />
      </button>

      {/* Align Right */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor
            ?.chain()
            .focus()
            .setTextAlign("right")
            .run()
        }
        className={`editor-tool ${
          descriptionEditor?.isActive({
            textAlign: "right",
          })
            ? "active"
            : ""
        }`}
        title="Align Right"
      >
        <AlignRight size={18} strokeWidth={2.3} />
      </button>

      <div className="editor-toolbar-divider" />

      {/* Undo */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor?.chain().focus().undo().run()
        }
        className="editor-tool"
        title="Undo"
      >
        ↶
      </button>

      {/* Redo */}
      <button
        type="button"
        onClick={() =>
          descriptionEditor?.chain().focus().redo().run()
        }
        className="editor-tool"
        title="Redo"
      >
        ↷
      </button>

      {/* Fullscreen */}
      <button
        type="button"
        onClick={() =>
          setIsEditorFullscreen(!isEditorFullscreen)
        }
        className="editor-tool"
        title={
          isEditorFullscreen
            ? "Exit Fullscreen"
            : "Fullscreen"
        }
      >
        {isEditorFullscreen ? (
          <Minimize2 size={18} strokeWidth={2.3} />
        ) : (
          <Maximize2 size={18} strokeWidth={2.3} />
        )}
      </button>

    </div>

    {/* Editor */}
    <EditorContent
      editor={descriptionEditor}
      className="course-description-content"
    />

    {/* Footer */}
    <div className="course-description-footer">
      Describe what learners will get from this package.
    </div>

  </div>
</div>     
      
      {/* Banner Photo Upload */}
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-gray-700">
          Banner photo <span className="text-red-500">*</span>
        </label>
        <div className="w-full border-2 border-dashed border-purple-300 rounded-xl hover:border-purple-400 transition-all duration-300 bg-gradient-to-br from-purple-50 to-blue-50">
          <input
            type="file"
            accept="image/*"
            onChange={handleBannerPhotoUpload}
            className="hidden"
            id="banner-photo-upload"
            required={!bannerPhotoPreviewUrl && !data.bannerUrl}
          />
          <label htmlFor="banner-photo-upload" className="w-full cursor-pointer block">
            {bannerPhotoPreviewUrl || data.bannerUrl ? (
              <div className="relative w-full aspect-[16/9] overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gray-100">
                  <img
                    src={bannerPhotoPreviewUrl || data.bannerUrl}
                    alt="Package banner photo preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setBannerPhotoPreviewUrl(null)
                    handleInputChange("bannerImage", null)
                    handleInputChange("bannerUrl", null)
                  }}
                  className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="relative w-full aspect-[16/9] overflow-hidden rounded-xl bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col items-center justify-center text-center mx-auto p-6 hover:from-purple-100 hover:to-blue-100 transition-all duration-300">
                <div className="bg-white rounded-full p-4 shadow-lg mb-4">
                  <Upload className="text-purple-600" size={32} />
                </div>
                <p className="text-gray-700 font-semibold text-base mb-2">Click to upload banner photo</p>
                <p className="text-sm text-gray-500 text-center">
                  Recommended: 1280x720px (16:9)<br />
                  <span className="text-xs">Max size: 10MB</span>
                </p>
              </div>
            )}
          </label>
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="submit"
          className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg font-semibold text-xs flex items-center space-x-1.5"
        >
          <span>Continue</span>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </form>
  )
}

export default PackageDetailsStep